import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  DatePicker,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Statistic,
  Select,
} from "antd";
import {
  EyeOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import JwtAxios from "apiServices";
import { GET_STYLE_LIST_ENDPOINT, PRODUCTION_PLANNING_REPORT, SEWING_LINES } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ListTable from "common/ListTable";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PlanningReport = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Default filter: current month start and end date
  const defaultStartDate = useMemo(() => moment().startOf("month"), []);
  const defaultEndDate = useMemo(() => moment().endOf("month"), []);

  const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
  const [sewingLines, setSewingLines] = useState([]);
  const [styleList, setStyleList] = useState([]);

  const getSewingLines = useCallback(async (filterValues) => {
    const response = await getData(SEWING_LINES, false, {
      per_page: 1000,
      ...filterValues,
    });
    if (response?.data?.code === 200) {
      setSewingLines(response?.data?.data?.data || []);
    }
  }, []);

  const getStyleList = useCallback(async (filterValues) => {
    const response = await getData(GET_STYLE_LIST_ENDPOINT, false, {
      per_page: 20,
      ...filterValues,
    });
    if (response?.data?.code === 200) {
      setStyleList(response?.data?.data?.data || []);
    }
  }, []);

  const buildFilterParams = useCallback(
    (customDates, extra = {}) => {
      const dates = customDates || dateRange;
      const values = form.getFieldsValue();
      return {
        start_date: dates?.[0] ? moment(dates[0]).format("YYYY-MM-DD") : null,
        end_date: dates?.[1] ? moment(dates[1]).format("YYYY-MM-DD") : null,
        ...(values?.line_id ? { line_id: values.line_id } : {}),
        ...(values?.style_id ? { style_id: values.style_id } : {}),
        ...extra,
      };
    },
    [dateRange, form],
  );

  // Fetch report data for Preview
  const handlePreview = useCallback(async (customDates) => {
    setLoading(true);
    setFetchError(null);

    const params = buildFilterParams(customDates);

    try {
      const response = await getData(PRODUCTION_PLANNING_REPORT, false, params);
      if (response && response?.data?.code === 200) {
        const payloadData = response?.data?.data?.data || [];
        const payloadSummary = response?.data?.data?.summary || null;
        setTableData(payloadData);
        setSummary(payloadSummary);
        message.success(`Preview loaded: ${payloadData.length} records found`);
      } else {
        setTableData([]);
        setSummary(null);
        setFetchError("No records found for the selected period.");
      }
    } catch (error) {
      console.error("Error previewing planning report:", error);
      setFetchError("Error fetching planning report preview.");
      message.error("Failed to fetch report preview");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  // Download Excel Report
  const handleDownload = async () => {
    setDownloading(true);

    const params = buildFilterParams(dateRange, { action: "download" });

    try {
      const response = await JwtAxios.get(PRODUCTION_PLANNING_REPORT, {
        params,
        responseType: "blob",
      });

      if (response && response?.data) {
        const fileName = `Planning_Report_${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`;
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        message.success("Planning Report Excel downloaded successfully");
      } else {
        message.error("Failed to download Excel report");
      }
    } catch (error) {
      console.error("Error downloading planning report:", error);
      message.error("Failed to download Excel report");
    } finally {
      setDownloading(false);
    }
  };

  // Reset to current month start and end date
  const handleReset = () => {
    const initialDates = [moment().startOf("month"), moment().endOf("month")];
    setDateRange(initialDates);
    form.setFieldsValue({
      date_range: initialDates,
      line_id: undefined,
      style_id: undefined,
    });
    getSewingLines();
    getStyleList();
    handlePreview(initialDates);
  };

  // Trigger preview on initial mount
  useEffect(() => {
    getSewingLines();
    getStyleList();
    handlePreview([defaultStartDate, defaultEndDate]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table columns definition (16 columns matching exact headings and order)
  const columns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date_formatted",
        key: "date",
        width: 105,
        fixed: "left",
        render: (text, record) => text || record?.date || "-",
      },
      {
        title: "Line",
        dataIndex: "line",
        key: "line",
        width: 110,
        fixed: "left",
        render: (text) => text || "-",
      },
      {
        title: "Buyer",
        dataIndex: "buyer",
        key: "buyer",
        width: 170,
        ellipsis: true,
        render: (text) => text || "-",
      },
      {
        title: "Subcontract Factory",
        dataIndex: "subcontract_factory",
        key: "subcontract_factory",
        width: 160,
        ellipsis: true,
        render: (text) => text || "-",
      },
      {
        title: "Merchandiser",
        dataIndex: "merchandiser",
        key: "merchandiser",
        width: 160,
        ellipsis: true,
        render: (text) => text || "-",
      },
      {
        title: "Style",
        dataIndex: "style",
        key: "style",
        width: 140,
        render: (text) => <strong>{text || "-"}</strong>,
      },
      {
        title: "Sub-style",
        dataIndex: "sub_style",
        key: "sub_style",
        width: 120,
        render: (text) => text || "-",
      },
      {
        title: "PO",
        dataIndex: "po",
        key: "po",
        width: 120,
        render: (text) => text || "-",
      },
      {
        title: "SAM",
        dataIndex: "sam",
        key: "sam",
        width: 85,
        align: "right",
        render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
      },
      {
        title: "MH",
        dataIndex: "mh",
        key: "mh",
        width: 80,
        align: "right",
        render: (val) => (val != null ? Number(val) : "-"),
      },
      {
        title: "Plan Qty",
        dataIndex: "plan_qty",
        key: "plan_qty",
        width: 105,
        align: "right",
        render: (val) => (val != null ? Number(val).toLocaleString() : "0"),
      },
      {
        title: "Actual Qty",
        dataIndex: "actual_qty",
        key: "actual_qty",
        width: 105,
        align: "right",
        render: (val) => (
          <span style={{ color: "#1890ff", fontWeight: 600 }}>
            {Number(val || 0).toLocaleString()}
          </span>
        ),
      },
      {
        title: "Hour",
        dataIndex: "hour",
        key: "hour",
        width: 80,
        align: "right",
        render: (val) => (val != null ? Number(val) : "-"),
      },
      {
        title: "Man Power",
        dataIndex: "man_power",
        key: "man_power",
        width: 100,
        align: "right",
        render: (val) => (val != null ? Number(val) : "-"),
      },
      {
        title: "Produce Minutes",
        dataIndex: "produce_minutes",
        key: "produce_minutes",
        width: 135,
        align: "right",
        render: (val) => (val != null ? Number(val).toLocaleString() : "0"),
      },
      {
        title: "Available min",
        dataIndex: "available_min",
        key: "available_min",
        width: 125,
        align: "right",
        render: (val) => (val != null ? Number(val).toLocaleString() : "0"),
      },
      {
        title: "Efficiency",
        dataIndex: "efficiency",
        key: "efficiency",
        width: 105,
        align: "right",
        fixed: "right",
        render: (val, record) => {
          const num = record?.efficiency_numeric || parseFloat(val) || 0;
          const color =
            num >= 60 ? "success" : num >= 45 ? "processing" : num >= 30 ? "warning" : "default";
          return (
            <Tag color={color} style={{ fontWeight: 600, minWidth: 60, textAlign: "center" }}>
              {val || "0.00%"}
            </Tag>
          );
        },
      },
    ],
    []
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          title={<Title level={5}>Production Planning Report</Title>}
          subTitle="Daily Planned vs Actual Tracking with Efficiency"
          extra={
            <Space>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                loading={loading}
                onClick={() => handlePreview()}
                style={{ borderRadius: "5px" }}
              >
                Preview
              </Button>
              <Button
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "#ffffff",
                  borderRadius: "5px",
                }}
                icon={<FileExcelOutlined />}
                loading={downloading}
                onClick={handleDownload}
              >
                Download Excel
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{ borderRadius: "5px" }}
              >
                Reset
              </Button>
            </Space>
          }
        />
      }
      content={
        <div style={{ padding: "0 15px 20px 15px" }}>
          {/* Filter Card */}
          <Card
            bordered={false}
            style={{
              marginBottom: 16,
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <Form form={form} layout="vertical">
              <Row gutter={16} align="bottom">
                <Col xs={24} sm={14} md={8} lg={6}>
                  <Form.Item
                    label={
                      <span>
                        <strong>Date Range</strong> (Defaults to Current Month)
                      </span>
                    }
                    name="date_range"
                    initialValue={[defaultStartDate, defaultEndDate]}
                    style={{ marginBottom: 0 }}
                  >
                    <RangePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      allowClear={false}
                      value={dateRange}
                      onChange={(dates) => {
                        if (dates && dates.length === 2) {
                          setDateRange(dates);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <Form.Item label={<strong>Line</strong>} name="line_id" style={{ marginBottom: 0 }}>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Line"
                      optionFilterProp="children"
                      onSearch={(value) => getSewingLines({ name: value })}
                      onClear={() => getSewingLines()}
                      filterOption={false}
                    >
                      {sewingLines.map((line) => (
                        <Select.Option value={line.id} key={line.id}>
                          {line.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <Form.Item label={<strong>Style</strong>} name="style_id" style={{ marginBottom: 0 }}>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Style"
                      optionFilterProp="children"
                      onSearch={(value) => getStyleList({ style_no: value })}
                      onClear={() => getStyleList()}
                      filterOption={false}
                    >
                      {styleList.map((style) => (
                        <Select.Option value={style.id} key={style.id}>
                          {style.style_no}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <Form.Item label=" " style={{ marginBottom: 0 }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={loading}
                        onClick={() => handlePreview()}
                        style={{ borderRadius: "5px" }}
                      >
                        Search
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleReset}
                        style={{ borderRadius: "5px" }}
                      >
                        Reset
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Summary KPI Cards */}
          {summary && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={8} md={4}>
                <Card size="small" bordered={false} style={{ borderRadius: "8px", background: "#f6ffed" }}>
                  <Statistic
                    title={<span style={{ fontSize: "12px", color: "#52c41a" }}>Total Records</span>}
                    value={summary.total_rows || 0}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Card size="small" bordered={false} style={{ borderRadius: "8px", background: "#e6f7ff" }}>
                  <Statistic
                    title={<span style={{ fontSize: "12px", color: "#1890ff" }}>Total Plan Qty</span>}
                    value={summary.total_plan_qty || 0}
                    precision={0}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Card size="small" bordered={false} style={{ borderRadius: "8px", background: "#f0f5ff" }}>
                  <Statistic
                    title={<span style={{ fontSize: "12px", color: "#2f54eb" }}>Total Actual Qty</span>}
                    value={summary.total_actual_qty || 0}
                    precision={0}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Card size="small" bordered={false} style={{ borderRadius: "8px", background: "#fff7e6" }}>
                  <Statistic
                    title={<span style={{ fontSize: "12px", color: "#fa8c16" }}>Total Produce Min</span>}
                    value={summary.total_produce_minutes || 0}
                    precision={0}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Card size="small" bordered={false} style={{ borderRadius: "8px", background: "#f9f0ff" }}>
                  <Statistic
                    title={<span style={{ fontSize: "12px", color: "#722ed1" }}>Avg Efficiency</span>}
                    value={summary.average_efficiency || "0.00%"}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Report Preview Table */}
          <div className="table" style={{ background: "#ffffff", padding: 12, borderRadius: "8px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 8, gap: 16 }}>
              <Space size={6}>
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    backgroundColor: "rgb(125 219 223 / 85%)",
                    border: "1px solid #7ddbdf",
                    borderRadius: 3,
                    verticalAlign: "middle",
                  }}
                />
                <span style={{ fontSize: 12 }}>Active</span>
              </Space>
              <Space size={6}>
                <span
                  style={{
                    display: "inline-block",
                    width: 14,
                    height: 14,
                    backgroundColor: "rgb(212 149 106 / 85%)",
                    border: "1px solid #d4956a",
                    borderRadius: 3,
                    verticalAlign: "middle",
                  }}
                />
                <span style={{ fontSize: 12 }}>Overwritten</span>
              </Space>
            </div>

            <style>{`
              .planning-report-table .planning-report-row-deleted > td {
                background-color: rgb(212 149 106 / 85%) !important;
              }
              .planning-report-table .planning-report-row-active > td {
                background-color: rgb(125 219 223 / 85%) !important;
              }
              .planning-report-table .planning-report-row-deleted:hover > td {
                background-color: rgb(212 149 106 / 95%) !important;
              }
              .planning-report-table .planning-report-row-active:hover > td {
                background-color: rgb(125 219 223 / 95%) !important;
              }
            `}</style>

            <ListTable
              className="planning-report-table"
              size="small"
              loading={loading}
              error={fetchError}
              onRetry={() => handlePreview()}
              emptyText="No planning records found for the selected date range."
              dataSource={tableData}
              columns={columns}
              rowKey={(record, index) =>
                `${record?.pp_initiate_id}_${record?.pp_initiate_detail_id}_${record?.po_map_id}_${index}`
              }
              rowClassName={(record) =>
                record?.is_deleted || record?.deleted_at
                  ? "planning-report-row-deleted"
                  : "planning-report-row-active"
              }
              scroll={{ x: 2000, y: 550 }}
              bordered
              sticky
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100", "200"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} records`,
              }}
            />
          </div>
        </div>
      }
    />
  );
};

export default PlanningReport;
